import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faTrash, faSpinner, faExclamationTriangle, faFileInvoice, faTruck, faFileContract, faBan } from '@fortawesome/free-solid-svg-icons';
import { getErrorMessage } from '../utils/errorMessages';

const DeleteConfirmationModal = ({ 
    isOpen, 
    onClose, 
    onConfirm, 
    title = "Confirm Deletion",
    message = "Are you sure you want to delete this item? This action cannot be undone.",
    itemName = "",
    loading = false,
    deleteButtonText = "Delete",
    blockingRecords = null
}) => {
    const [error, setError] = useState('');
    
    // Check if this is a blocking scenario (product is locked)
    const isBlocked = blockingRecords && (
        blockingRecords.deliveries?.count > 0 ||
        blockingRecords.trade_recaps?.count > 0 ||
        blockingRecords.invoices?.count > 0
    );

    const handleConfirm = async () => {
        try {
            setError('');
            await onConfirm();
        } catch (err) {
            console.error('Delete operation failed:', err);
            setError(getErrorMessage(err, 'Delete operation failed. Please try again.'));
        }
    };

    const handleClose = () => {
        if (!loading) {
            setError('');
            onClose();
        }
    };

    if (!isOpen) {
        return null;
    }

    const modalContent = (
        <div 
            className="modal-overlay"
            style={{ 
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: 10000, 
                backgroundColor: 'rgba(0,0,0,0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
            }}
            onClick={handleClose}
        >
            <div 
                className="modal-content delete-modal" 
                style={{ 
                    zIndex: 10001,
                    backgroundColor: 'white',
                    padding: '2rem',
                    borderRadius: '8px',
                    maxWidth: '500px',
                    width: '90vw',
                    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
                }}
                onClick={(e) => e.stopPropagation()}
            >
                <div className="modal-header" style={{ position: 'relative', marginBottom: '1.5rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                        <div style={{ 
                            width: '3rem', 
                            height: '3rem', 
                            borderRadius: '50%', 
                            backgroundColor: isBlocked ? '#fef3c7' : '#fee2e2',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}>
                            <FontAwesomeIcon 
                                icon={isBlocked ? faBan : faExclamationTriangle} 
                                style={{ color: isBlocked ? '#d97706' : '#dc2626', fontSize: '1.25rem' }} 
                            />
                        </div>
                        <h3 style={{ margin: 0, color: '#111827', fontSize: '1.125rem', fontWeight: '600' }}>
                            {isBlocked ? 'Product Cannot Be Deleted' : title}
                        </h3>
                    </div>
                    <button 
                        className="modal-close-btn" 
                        onClick={handleClose}
                        disabled={loading}
                        style={{
                            position: 'absolute',
                            top: '0',
                            right: '0',
                            border: 'none',
                            background: 'none',
                            fontSize: '1.5rem',
                            cursor: loading ? 'not-allowed' : 'pointer',
                            color: '#666',
                            opacity: loading ? 0.5 : 1
                        }}
                    >
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                </div>
                
                <div className="modal-body" style={{ marginBottom: '2rem' }}>
                    {isBlocked ? (
                        <>
                            <div style={{ 
                                backgroundColor: '#fffbeb', 
                                padding: '1rem', 
                                borderRadius: '0.375rem',
                                border: '1px solid #fcd34d',
                                marginBottom: '1rem'
                            }}>
                                <p style={{ margin: '0 0 0.75rem 0', color: '#92400e', fontWeight: '500', lineHeight: '1.5' }}>
                                    This product is locked into the system and cannot be deleted.
                                </p>
                                <p style={{ margin: 0, color: '#78350f', fontSize: '0.875rem', lineHeight: '1.5' }}>
                                    {error || 'The product is currently being used in the following records:'}
                                </p>
                            </div>
                            
                            {itemName && (
                                <div style={{ 
                                    backgroundColor: '#f9fafb', 
                                    padding: '0.75rem 1rem', 
                                    borderRadius: '0.375rem',
                                    border: '1px solid #e5e7eb',
                                    marginBottom: '1rem'
                                }}>
                                    <p style={{ margin: 0, fontWeight: '600', color: '#111827', fontSize: '0.875rem' }}>Product:</p>
                                    <p style={{ margin: '0.25rem 0 0 0', color: '#374151' }}>{itemName}</p>
                                </div>
                            )}
                            
                            <div style={{ 
                                backgroundColor: '#fff', 
                                border: '1px solid #e5e7eb',
                                borderRadius: '0.375rem',
                                overflow: 'hidden'
                            }}>
                                <div style={{ 
                                    backgroundColor: '#f9fafb', 
                                    padding: '0.75rem 1rem',
                                    borderBottom: '1px solid #e5e7eb'
                                }}>
                                    <p style={{ margin: 0, fontWeight: '600', color: '#111827', fontSize: '0.875rem' }}>
                                        Linked Records
                                    </p>
                                </div>
                                
                                <div style={{ padding: '0.75rem' }}>
                                    {blockingRecords.deliveries?.count > 0 && (
                                        <div style={{ 
                                            display: 'flex', 
                                            alignItems: 'flex-start', 
                                            gap: '0.75rem',
                                            padding: '0.75rem',
                                            backgroundColor: '#fef3c7',
                                            borderRadius: '0.375rem',
                                            marginBottom: '0.75rem',
                                            border: '1px solid #fde68a'
                                        }}>
                                            <FontAwesomeIcon icon={faTruck} style={{ color: '#d97706', marginTop: '0.125rem', fontSize: '1.125rem' }} />
                                            <div style={{ flex: 1 }}>
                                                <p style={{ margin: '0 0 0.25rem 0', fontWeight: '600', color: '#92400e', fontSize: '0.875rem' }}>
                                                    {blockingRecords.deliveries.count} Delivery{blockingRecords.deliveries.count !== 1 ? ' Records' : ''}
                                                </p>
                                                {blockingRecords.deliveries.examples?.length > 0 && (
                                                    <p style={{ margin: 0, color: '#78350f', fontSize: '0.75rem' }}>
                                                        Examples: {blockingRecords.deliveries.examples.join(', ')}
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                    
                                    {blockingRecords.trade_recaps?.count > 0 && (
                                        <div style={{ 
                                            display: 'flex', 
                                            alignItems: 'flex-start', 
                                            gap: '0.75rem',
                                            padding: '0.75rem',
                                            backgroundColor: '#dbeafe',
                                            borderRadius: '0.375rem',
                                            marginBottom: '0.75rem',
                                            border: '1px solid #bfdbfe'
                                        }}>
                                            <FontAwesomeIcon icon={faFileContract} style={{ color: '#2563eb', marginTop: '0.125rem', fontSize: '1.125rem' }} />
                                            <div style={{ flex: 1 }}>
                                                <p style={{ margin: '0 0 0.25rem 0', fontWeight: '600', color: '#1e3a8a', fontSize: '0.875rem' }}>
                                                    {blockingRecords.trade_recaps.count} Trade Recap{blockingRecords.trade_recaps.count !== 1 ? 's' : ''}
                                                </p>
                                                {blockingRecords.trade_recaps.examples?.length > 0 && (
                                                    <p style={{ margin: 0, color: '#1e40af', fontSize: '0.75rem' }}>
                                                        Examples: {blockingRecords.trade_recaps.examples.join(', ')}
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                    
                                    {blockingRecords.invoices?.count > 0 && (
                                        <div style={{ 
                                            display: 'flex', 
                                            alignItems: 'flex-start', 
                                            gap: '0.75rem',
                                            padding: '0.75rem',
                                            backgroundColor: '#dcfce7',
                                            borderRadius: '0.375rem',
                                            border: '1px solid #bbf7d0'
                                        }}>
                                            <FontAwesomeIcon icon={faFileInvoice} style={{ color: '#16a34a', marginTop: '0.125rem', fontSize: '1.125rem' }} />
                                            <div style={{ flex: 1 }}>
                                                <p style={{ margin: '0 0 0.25rem 0', fontWeight: '600', color: '#14532d', fontSize: '0.875rem' }}>
                                                    {blockingRecords.invoices.count} Invoice{blockingRecords.invoices.count !== 1 ? 's' : ''}
                                                </p>
                                                {blockingRecords.invoices.examples?.length > 0 && (
                                                    <p style={{ margin: 0, color: '#15803d', fontSize: '0.75rem' }}>
                                                        Examples: {blockingRecords.invoices.examples.join(', ')}
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </>
                    ) : (
                        <>
                            <p style={{ margin: '0 0 1rem 0', color: '#374151', lineHeight: '1.5' }}>
                                {message}
                            </p>
                            {itemName && (
                                <div style={{ 
                                    backgroundColor: '#f9fafb', 
                                    padding: '0.75rem 1rem', 
                                    borderRadius: '0.375rem',
                                    border: '1px solid #e5e7eb'
                                }}>
                                    <p style={{ margin: 0, fontWeight: '500', color: '#111827' }}>{itemName}</p>
                                </div>
                            )}
                            {error && (
                                <div style={{ 
                                    backgroundColor: '#fee2e2', 
                                    color: '#991b1b', 
                                    padding: '0.75rem', 
                                    borderRadius: '0.375rem', 
                                    marginTop: '1rem',
                                    fontSize: '0.875rem'
                                }}>
                                    {error}
                                </div>
                            )}
                        </>
                    )}
                </div>
                
                <div className="modal-actions" style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center' }}>
                    {isBlocked ? (
                        <button
                            type="button"
                            className="btn btn-primary"
                            onClick={handleClose}
                            style={{ 
                                padding: '0.75rem 2rem', 
                                backgroundColor: '#3b82f6', 
                                color: 'white',
                                border: 'none',
                                borderRadius: '0.375rem',
                                cursor: 'pointer',
                                fontWeight: '500'
                            }}
                        >
                            Close
                        </button>
                    ) : (
                        <>
                            <button
                                type="button"
                                className="btn btn-secondary"
                                onClick={handleClose}
                                disabled={loading}
                                style={{ 
                                    padding: '0.75rem 1.5rem', 
                                    border: '1px solid #d1d5db', 
                                    backgroundColor: '#f9fafb', 
                                    color: '#374151',
                                    borderRadius: '0.375rem',
                                    cursor: loading ? 'not-allowed' : 'pointer',
                                    opacity: loading ? 0.5 : 1
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                className="btn btn-danger"
                                onClick={handleConfirm}
                                disabled={loading}
                                style={{ 
                                    padding: '0.75rem 1.5rem', 
                                    backgroundColor: loading ? '#f87171' : '#dc2626', 
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '0.375rem',
                                    cursor: loading ? 'not-allowed' : 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.5rem',
                                    opacity: loading ? 0.8 : 1
                                }}
                            >
                                {loading ? (
                                    <>
                                        <FontAwesomeIcon icon={faSpinner} spin />
                                        Deleting...
                                    </>
                                ) : (
                                    <>
                                        <FontAwesomeIcon icon={faTrash} />
                                        {deleteButtonText}
                                    </>
                                )}
                            </button>
                        </>
                    )}
                </div>
            </div>
        </div>
    );

    // Render modal using Portal to document.body
    return ReactDOM.createPortal(modalContent, document.body);
};

export default DeleteConfirmationModal; 